import React, { useState, useEffect } from 'react';
import {
  PhotoIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilSquareIcon,
  TrashIcon,
  GlobeAltIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { Gallery } from '../../types/gallery';
import { galleryService } from '../../services/galleryService';
import { useAuth } from '../../contexts/AuthContext';
import GalleryEditor from './GalleryEditor';
import GalleryDetails from './GalleryDetails';
import { formatDate } from '../../utils/dateHelpers';
import CommonPageHeader from '../common/CommonPageHeader';
import SearchBar from '../common/SearchBar';
import LoadingScreen from '../common/LoadingScreen';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';

const Galleries: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [publishedFilter, setPublishedFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [showEditor, setShowEditor] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState<Gallery | null>(null);
  const [galleryToDelete, setGalleryToDelete] = useState<Gallery | null>(null);

  useEffect(() => {
    loadGalleries();
  }, [user]);

  const loadGalleries = async () => {
    if (!user?.organizationId) return;
    
    try {
      setLoading(true);
      const data = await galleryService.getGalleries(user.organizationId);
      setGalleries(data);
    } catch (error) {
      console.error('Error loading galleries:', error);
      toast.error('Hiba a galériák betöltése során');
    } finally {
      setLoading(false);
    }
  };

  // Szűrés
  const filteredGalleries = galleries.filter(gallery => {
    const matchesSearch = 
      gallery.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      gallery.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      gallery.slug.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesPublished = 
      publishedFilter === 'all' ||
      (publishedFilter === 'published' && gallery.isPublished) ||
      (publishedFilter === 'draft' && !gallery.isPublished);

    return matchesSearch && matchesPublished;
  });

  const handleEdit = (gallery: Gallery) => {
    setSelectedGallery(gallery);
    setShowEditor(true);
  };

  const handleView = (gallery: Gallery) => {
    setSelectedGallery(gallery);
    setShowDetails(true);
  };

  const handleDelete = (gallery: Gallery) => {
    setGalleryToDelete(gallery);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!galleryToDelete) return;

    try {
      await galleryService.deleteGallery(galleryToDelete.id);
      toast.success('Galéria sikeresen törölve');
      setShowDeleteModal(false);
      setGalleryToDelete(null);
      loadGalleries();
    } catch (error: any) {
      toast.error(`Hiba: ${error.message}`);
    }
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setSelectedGallery(null);
    loadGalleries();
  };

  const handleDetailsClose = () => {
    setShowDetails(false);
    setSelectedGallery(null);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <CommonPageHeader
        title="Galériák"
        subtitle="Képgalériák kezelése a weboldalhoz"
        icon={PhotoIcon}
      >
        <button
          onClick={() => {
            setSelectedGallery(null);
            setShowEditor(true);
          }}
          className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          <span>Új galéria</span>
        </button>
      </CommonPageHeader>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Keresés cím, leírás vagy slug alapján..."
            />
          </div>

          {/* Published Filter */}
          <div className="sm:w-48">
            <select
              value={publishedFilter}
              onChange={(e) => setPublishedFilter(e.target.value as any)}
              className="w-full pl-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="all">Összes állapot</option>
              <option value="published">Publikált</option>
              <option value="draft">Piszkozat</option>
            </select>
          </div>
        </div>
      </div>

      {/* Gallery Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredGalleries.map((gallery) => (
          <div
            key={gallery.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
          >
            {/* Cover Image */}
            <div className="relative h-48 bg-gray-200 dark:bg-gray-700">
              {gallery.coverImageUrl || gallery.images[0]?.url ? (
                <img
                  src={gallery.coverImageUrl || gallery.images[0]?.url}
                  alt={gallery.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <PhotoIcon className="w-16 h-16 text-gray-400" />
                </div>
              )}
              
              {/* Published Badge */}
              <div className="absolute top-2 right-2">
                {gallery.isPublished ? (
                  <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                    <GlobeAltIcon className="w-3 h-3" />
                    Publikált
                  </span>
                ) : (
                  <span className="bg-gray-500 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                    <EyeSlashIcon className="w-3 h-3" />
                    Piszkozat
                  </span>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {gallery.title}
              </h3>
              
              {gallery.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                  {gallery.description}
                </p>
              )}

              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-4">
                <span>{gallery.images.length} kép</span>
                <span>{formatDate(gallery.createdAt)}</span>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleView(gallery)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <EyeIcon className="w-4 h-4" />
                  Megtekintés
                </button>
                <button
                  onClick={() => handleEdit(gallery)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                  <PencilSquareIcon className="w-4 h-4" />
                  Szerkesztés
                </button>
                <button
                  onClick={() => handleDelete(gallery)}
                  className="px-3 py-2 text-sm bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-md hover:bg-red-200 dark:hover:bg-red-900/40 transition-colors"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredGalleries.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
          <PhotoIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Nincs galéria
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {searchTerm ? 'Nincs találat a keresési feltételeknek megfelelően.' : 'Kezdj el galériákat létrehozni!'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => {
                setSelectedGallery(null);
                setShowEditor(true);
              }}
              className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors inline-flex items-center gap-2"
            >
              <PlusIcon className="w-5 h-5" />
              Új galéria létrehozása
            </button>
          )}
        </div>
      )}

      {/* Modals */}
      {showEditor && (
        <GalleryEditor
          gallery={selectedGallery}
          onClose={handleEditorClose}
        />
      )}

      {showDetails && selectedGallery && (
        <GalleryDetails
          gallery={selectedGallery}
          onClose={handleDetailsClose}
          onEdit={() => {
            setShowDetails(false);
            setShowEditor(true);
          }}
        />
      )}

      {showDeleteModal && galleryToDelete && (
        <ConfirmDeleteModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setGalleryToDelete(null);
          }}
          onConfirm={confirmDelete}
          title="Galéria törlése"
          deleteMessage={`Biztosan törölni szeretnéd a(z) "${galleryToDelete.title}" galériát?`}
          deleteWarning="Ez a művelet nem vonható vissza."
        />
      )}
    </div>
  );
};

export default Galleries;
